import React, { useState, useMemo, useEffect } from 'react';
import { User } from '../../types';
import { gameService } from '../../services/gameService';
import { useError } from '../../contexts/ErrorContext';

interface CrimePanelProps {
  user: User;
  onCrimeComplete: () => void;
}

const CrimePanel: React.FC<CrimePanelProps> = ({ user, onCrimeComplete }) => {
  const [loadingCrimes, setLoadingCrimes] = useState<{ [key: number]: boolean }>({});
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [tooltipVisible, setTooltipVisible] = useState<{ [key: number]: boolean }>({});
  const [currentRewards, setCurrentRewards] = useState<{ [key: number]: { money: number; rankPoints: number } }>({});
  const { showError, showSuccess } = useError();

  // Helper function to get rank name
  const getRankName = (rankId: number) => {
    const ranks = [
      'Street Thug', 'Gang Member', 'Dealer', 'Enforcer', 'Lieutenant',
      'Underboss', 'Boss', 'Don', 'Godfather', 'Legend'
    ];
    return ranks[Math.min(rankId - 1, ranks.length - 1)] || 'Unknown';
  };

  // Helper function to format time in human-readable format
  const formatTime = (minutes: number): string => {
    const mins = Math.floor(minutes);
    const secs = Math.round((minutes - mins) * 60);
    
    if (mins === 0) {
      return `${secs}s`;
    } else if (secs === 0) {
      return `${mins}m`;
    } else {
      return `${mins}m ${secs}s`;
    }
  };

  // Helper function to calculate success rate
  const getSuccessRate = (crimeId: number) => {
    const baseRates = {
      1: 85, // Beg
      2: 75, // Hustle
      3: 65, // Mug
      4: 55, // Rob
      5: 45, // Kidnap
      6: 35, // Bank Heist
      7: 25  // Steal Bullets
    };
    
    const baseRate = baseRates[crimeId as keyof typeof baseRates] || 50;
    const rankBonus = (user.rankId - crimeId) * 2; // Higher rank = better success
    return Math.min(baseRate + rankBonus, 95); // Cap at 95%
  };

  // Helper function to get random crime rewards
  const getCrimeRewards = (crimeId: number) => {
    const crime = crimes.find(c => c.id === crimeId);
    if (!crime) return { money: 100, rankPoints: 2 };
    
    // Generate random money reward within the range
    const money = Math.floor(Math.random() * (crime.moneyRange.max - crime.moneyRange.min + 1)) + crime.moneyRange.min;
    
    // Generate random rank points within the range
    const rankPoints = Math.floor(Math.random() * (crime.rankPointsRange.max - crime.rankPointsRange.min + 1)) + crime.rankPointsRange.min;
    
    return { money, rankPoints };
  };



  // Toggle tooltip visibility
  const toggleTooltip = (crimeId: number) => {
    setTooltipVisible(prev => ({
      ...prev,
      [crimeId]: !prev[crimeId]
    }));
  };

  // Update current time every second for countdown
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const crimes = useMemo(() => [
    { id: 1, name: 'Beg', minRank: 1, description: 'Beg for money on the streets', cooldownMinutes: 1, rankPoints: 1, image: '/images/crimes/beg.jpg', moneyRange: { min: 25, max: 75 }, rankPointsRange: { min: 1, max: 2 } },
    { id: 2, name: 'Hustle', minRank: 2, description: 'Hustle people for money', cooldownMinutes: 1.17, rankPoints: 2, image: '/images/crimes/hustle.jpg', moneyRange: { min: 75, max: 150 }, rankPointsRange: { min: 2, max: 3 } },
    { id: 3, name: 'Mug', minRank: 3, description: 'Mug someone for their money', cooldownMinutes: 1.33, rankPoints: 3, image: '/images/crimes/mug.jpg', moneyRange: { min: 150, max: 300 }, rankPointsRange: { min: 3, max: 4 } },
    { id: 4, name: 'Rob', minRank: 4, description: 'Rob a store', cooldownMinutes: 1.5, rankPoints: 4, image: '/images/crimes/rob.jpg', moneyRange: { min: 250, max: 400 }, rankPointsRange: { min: 4, max: 5 } },
    { id: 5, name: 'Kidnap', minRank: 5, description: 'Kidnap someone for ransom', cooldownMinutes: 1.67, rankPoints: 5, image: '/images/crimes/kidnap.jpg', moneyRange: { min: 400, max: 650 }, rankPointsRange: { min: 5, max: 7 } },
    { id: 6, name: 'Bank Heist', minRank: 6, description: 'Execute a sophisticated bank robbery', cooldownMinutes: 1.83, rankPoints: 6, image: '/images/crimes/bank.jpg', moneyRange: { min: 650, max: 1000 }, rankPointsRange: { min: 6, max: 8 } },
    { id: 7, name: 'Steal Bullets', minRank: 10, description: 'Raid a gun store for bullets', cooldownMinutes: 2, rankPoints: 10, image: '/images/crimes/bullets.jpg', moneyRange: { min: 800, max: 1200 }, rankPointsRange: { min: 8, max: 12 } }
  ], []);

  // Memoize available crimes calculation
  const availableCrimes = useMemo(() => {
    return crimes.filter(crime => user.rankId >= crime.minRank && user.status === 'Alive');
  }, [user.rankId, user.status, crimes]);

  const handleCrime = async (crimeId: number) => {
    setLoadingCrimes(prev => ({ ...prev, [crimeId]: true }));

    // Generate random rewards for this crime attempt
    const rewards = getCrimeRewards(crimeId);
    setCurrentRewards(prev => ({ ...prev, [crimeId]: rewards }));

    try {
      const response = await gameService.commitCrime({ crimeType: crimeId });
      
      if (response.success) {
        // Show success message with the actual rewards earned
        const successMessage = `Successfully committed ${crimes.find(c => c.id === crimeId)?.name}! Earned $${rewards.money} and ${rewards.rankPoints} rank points.`;
        showSuccess(successMessage);
        // Refresh user data immediately
        onCrimeComplete();
      } else {
        showError(response.message);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'An error occurred while committing the crime';
      showError(errorMessage);
    } finally {
      setLoadingCrimes(prev => ({ ...prev, [crimeId]: false }));
    }
  };

  const canCommitCrime = (crime: any): boolean => {
    return availableCrimes.some(availableCrime => availableCrime.id === crime.id);
  };

  // Universal cooldown check - based on the last crime committed
  const getUniversalCooldown = (): number | null => {
    console.log(`🔍 Frontend universal cooldown check:`);
    console.log(`⏰ User lastCrimeTime: ${user.lastCrimeTime}`);
    console.log(`🎯 User lastCrimeType: ${user.lastCrimeType}`);
    
    if (!user.lastCrimeTime) {
      console.log(`✅ No lastCrimeTime - no cooldown`);
      return null;
    }
    
    // If we don't have lastCrimeType, use a default cooldown of 1 minute
    let cooldownMinutes = 1;
    if (user.lastCrimeType) {
      const lastCrime = crimes.find(c => c.id === user.lastCrimeType);
      if (lastCrime) {
        cooldownMinutes = lastCrime.cooldownMinutes;
      }
    }
    
    const cooldownMs = cooldownMinutes * 60 * 1000;
    const timeSinceLastCrime = currentTime - new Date(user.lastCrimeTime).getTime();
    
    console.log(`📊 Cooldown minutes: ${cooldownMinutes}`);
    console.log(`⏱️ Time since last crime: ${timeSinceLastCrime}ms`);
    console.log(`⏳ Cooldown required: ${cooldownMs}ms`);
    
    if (timeSinceLastCrime < cooldownMs) {
      const remaining = cooldownMs - timeSinceLastCrime;
      console.log(`🚫 Universal cooldown active! Remaining: ${remaining}ms`);
      return remaining;
    }
    
    console.log(`✅ Universal cooldown passed`);
    return null;
  };

  const formatCooldown = (ms: number): string => {
    const minutes = ms / (60 * 1000);
    return formatTime(minutes);
  };

  // Get universal cooldown once for all crimes
  const universalCooldown = getUniversalCooldown();
  const isOnUniversalCooldown = universalCooldown !== null;

  // Get the name of the last committed crime for display
  const getLastCrimeName = (): string => {
    if (!user.lastCrimeType) return 'your last crime';
    const lastCrime = crimes.find(c => c.id === user.lastCrimeType);
    return lastCrime ? lastCrime.name : 'your last crime';
  };

  return (
    <div className="crime-panel">
      <h3 className="mb-3">Crimes</h3>
      


      {user.status === 'Dead' && (
        <div className="alert alert-danger mb-3" style={{ 
          background: 'var(--error-color)', 
          padding: '12px', 
          borderRadius: 'var(--border-radius)',
          color: 'white'
        }}>
          You are dead and cannot commit crimes!
        </div>
      )}

      {/* Universal cooldown countdown timer */}
      {isOnUniversalCooldown && (
        <div className="alert alert-warning mb-3" style={{ 
          background: 'var(--warning-color)', 
          padding: '12px', 
          borderRadius: 'var(--border-radius)',
          color: 'white',
          textAlign: 'center',
          fontWeight: 'bold',
          fontSize: '16px'
        }}>
          ⏰ Universal Crime Cooldown: <span style={{ fontSize: '18px', fontWeight: 'bold' }}>{formatCooldown(universalCooldown!)}</span>
          <br />
          <small>All crimes are temporarily unavailable after committing {getLastCrimeName()}</small>
        </div>
      )}

      <div className="crimes-grid">
        {crimes.map((crime) => {
          const canCommit = canCommitCrime(crime) && !isOnUniversalCooldown;
          const isLoading = loadingCrimes[crime.id];
          const successRate = getSuccessRate(crime.id);
          
          return (
            <div 
              key={crime.id}
              className={`crime-card ${!canCommit ? 'disabled' : ''}`}
            >
              {/* Crime Image */}
              <div className="crime-image-container">
                <img 
                  src={crime.image} 
                  alt={crime.name}
                  className="crime-image"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
                
                {/* Rank Badge Overlay */}
                <div className="rank-badge">
                  Min Rank: {getRankName(crime.minRank)}
                </div>
              </div>

              {/* Crime Content */}
              <div className="crime-content">
                <h4 className="crime-name">
                  {crime.name}
                  <span className="success-value">{successRate}%</span>
                </h4>
                
                {/* Error Message */}
                {!canCommitCrime(crime) && !isOnUniversalCooldown && (
                  <div className="crime-error">
                    {user.status === 'Dead' ? 'You are dead' : `Requires rank ${crime.minRank}`}
                  </div>
                )}

                {/* Button Container with Info Icon */}
                <div className="button-container">
                  <button 
                    className={`crime-button ${!canCommit ? 'disabled' : ''}`}
                    onClick={() => handleCrime(crime.id)}
                    disabled={isLoading || !canCommit || user.status === 'Dead'}
                  >
                    {isLoading ? 'Committing...' : 'Commit'}
                  </button>
                  
                  <div className="info-icon-container" style={{ position: 'relative' }}>
                    <div 
                      className="info-icon"
                      onClick={() => toggleTooltip(crime.id)}
                    >
                      i
                    </div>
                    
                    {/* Info Tooltip */}
                    <div className={`info-tooltip ${tooltipVisible[crime.id] ? 'visible' : ''}`}>
                      <div className="tooltip-title">{crime.name} Details</div>
                      <div className="tooltip-item">
                        <span className="tooltip-label">Money Reward:</span>
                        <span className="tooltip-value">
                          {currentRewards[crime.id] ? 
                            `$${currentRewards[crime.id].money}` : 
                            `$${crime.moneyRange.min}-${crime.moneyRange.max}`
                          }
                        </span>
                      </div>
                      <div className="tooltip-item">
                        <span className="tooltip-label">Rank Points:</span>
                        <span className="tooltip-value">
                          {currentRewards[crime.id] ? 
                            currentRewards[crime.id].rankPoints : 
                            `${crime.rankPointsRange.min}-${crime.rankPointsRange.max}`
                          }
                        </span>
                      </div>
                      <div className="tooltip-item">
                        <span className="tooltip-label">Cooldown:</span>
                        <span className="tooltip-value">{formatTime(crime.cooldownMinutes)}</span>
                      </div>
                      <div className="tooltip-item">
                        <span className="tooltip-label">Success Rate:</span>
                        <span className="tooltip-value">{successRate}%</span>
                      </div>
                      {currentRewards[crime.id] && (
                        <div className="tooltip-item" style={{ borderTop: '1px solid var(--accent-color)', marginTop: '8px', paddingTop: '8px' }}>
                          <span className="tooltip-label" style={{ color: 'var(--accent-color)' }}>Current Attempt:</span>
                          <span className="tooltip-value" style={{ color: 'var(--accent-color)' }}>Ready to commit</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CrimePanel;
